import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

function toWindowFeatures(obj) {
  return Object.keys(obj)
    .reduce((features, name) => {
      const value = obj[name];
      if (typeof value === 'boolean') {
        features.push(`${name}=${value ? 'yes' : 'no'}`);
      } else {
        features.push(`${name}=${value}`);
      }
      return features;
    }, [])
    .join(',');
}

/**
 * Opens new window and within react portal.
 * Logic is taken partially from the react-new-window package.
 */
class NewWindow extends React.PureComponent {
  /**
   * NewWindow default props.
   */
  static defaultProps = {
    url: '',
    name: '',
    title: '',
    features: { width: '600px', height: '640px' },
    center: 'parent',
  };

  static propTypes = {
    children: PropTypes.node,
    url: PropTypes.string,
    name: PropTypes.string,
    title: PropTypes.string,
    features: PropTypes.object,
    onUnload: PropTypes.func,
    onBlock: PropTypes.func,
    onMounted: PropTypes.func,
    center: PropTypes.oneOf(['parent', 'screen']),
  };

  constructor(props) {
    super(props);
    this.state = {
      mounted: false,
    };
  }

  componentDidMount() {
    this.openChild();
  }

  componentDidUpdate() {
    if (!this.notifiedParent) {
      this.props.onMounted();
    }
  }

  componentWillUnmount() {
    if (this.window) {
      this.window.close();
    }
  }

  onWindowLoaded = () => {
    const { title } = this.props;
    this.window.document.title = title;
    this.container = this.window.document.createElement('div');
    this.window.document.body.appendChild(this.container);
    this.window.focus();
    this.setState({ mounted: true });
  };

  openChild = () => {
    const { url, name, features, onBlock, center } = this.props;

    // Prepare position of the new window to be centered against the 'parent' window or 'screen'.
    if (typeof center === 'string' &&
      (features.width === undefined || features.height === undefined)
    ) {
      // eslint-disable-next-line
      console.warn(
        'width and height window features must be present when a center prop is provided',
      );
    } else if (center === 'parent') {
      features.left =
        ((window.top.outerWidth / 2) + window.top.screenX) - (features.width / 2);
      features.top =
        ((window.top.outerHeight / 2) + window.top.screenY) - (features.height / 2);
    } else if (center === 'screen') {
      const screenLeft =
        window.screenLeft !== undefined ? window.screenLeft : window.screen.left;
      const screenTop =
        window.screenTop !== undefined ? window.screenTop : window.screen.top;

      // eslint-disable-next-line
      const width = window.innerWidth
        ? window.innerWidth
        : document.documentElement.clientWidth
          ? document.documentElement.clientWidth
          : window.screen.width;
      // eslint-disable-next-line
      const height = window.innerHeight
        ? window.innerHeight
        : document.documentElement.clientHeight
          ? document.documentElement.clientHeight
          : window.screen.height;

      features.left = ((width / 2) - (features.width / 2)) + screenLeft;
      features.top = ((height / 2) - (features.height / 2)) + screenTop;
    }

    this.window = window.open(url, name, toWindowFeatures(features));

    if (!this.window) {
      if (typeof onBlock === 'function') {
        onBlock.call(null);
      } else {
        // eslint-disable-next-line
        console.warn('A new window could not be opened. Maybe it was blocked.')
      }
    } else {
      // When a new window use content from a cross-origin there's no way we can attach event
      // to it. Therefore, we need to detect in a interval when the new window was destroyed
      // or was closed.
      this.windowCheckerInterval = setInterval(() => {
        if (!this.window || this.window.closed) {
          this.release();
        } else if (!this.windowLoaded && this.window.document && this.window.document.body) {
          this.windowLoaded = true;
          this.onWindowLoaded();
        }
      }, 50);
    }
  };

  /**
   * Release the new window and anything that was bound to it.
   */
  release = () => {
    // This method can be called once.
    if (this.released) {
      return;
    }
    this.released = true;

    // Remove checker interval.
    clearInterval(this.windowCheckerInterval);

    // Call any function bound to the `onUnload` prop.
    const { onUnload } = this.props;

    if (typeof onUnload === 'function') {
      onUnload.call(null);
    }
  };

  /**
   * Render the NewWindow component.
   */
  render() {
    const { mounted } = this.state;
    const { children } = this.props;
    if (!mounted) {
      return null;
    }
    return ReactDOM.createPortal(children, this.container);
  }
}

export default NewWindow;



// WEBPACK FOOTER //
// ./src/app/newWindow.js